import { createContext, memo, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useSaleOrderSummary } from '../hooks/use-sale-order-summary';
import { useSalePinsData } from '../hooks/use-sale-pins';
import { useSaleProductsData } from '../hooks/use-sale-products';
import { useSaleSidebarData as useSaleSidebarDataHook } from '../hooks/use-sale-sidebar-data';
import { useSaleVariants } from '../hooks/use-sale-variants';
import type { SaleInvalidationActions, SaleInvalidationOptions } from '../hooks/use-sale-invalidation';

type SaleProductsContextValue = ReturnType<typeof useSaleProductsData> & { alphabets: string[] };
type SalePinsContextValue = ReturnType<typeof useSalePinsData>;
type SaleSidebarContextValue = ReturnType<typeof useSaleSidebarDataHook>;
type SaleOrderSummaryContextValue = ReturnType<typeof useSaleOrderSummary>;

const SaleProductsContext = createContext<SaleProductsContextValue | null>(null);
const SalePinsContext = createContext<SalePinsContextValue | null>(null);
const SaleSidebarContext = createContext<SaleSidebarContextValue | null>(null);
const SaleOrderSummaryContext = createContext<SaleOrderSummaryContextValue | null>(null);

type SaleProvidersProps = {
  children: ReactNode;
  scheduleInvalidation?: (options?: SaleInvalidationOptions) => void;
  registerActions?: (actions: SaleInvalidationActions) => void;
};

function SaleOrderSummaryProvider({ children }: { children: ReactNode }) {
  const orderSummary = useSaleOrderSummary();
  const value = useMemo(
    () => orderSummary,
    [
      orderSummary.totalOrders,
      orderSummary.totalItems,
      orderSummary.paidAmount,
      orderSummary.currency,
      orderSummary.isLoading,
      orderSummary.refresh,
      orderSummary.mutate,
    ],
  );
  return <SaleOrderSummaryContext.Provider value={value}>{children}</SaleOrderSummaryContext.Provider>;
}

function SaleSidebarProvider({
  children,
  scheduleInvalidation,
}: {
  children: ReactNode;
  scheduleInvalidation?: (options?: SaleInvalidationOptions) => void;
}) {
  const sidebar = useSaleSidebarDataHook({ scheduleInvalidation });
  const value = useMemo(
    () => sidebar,
    [
      sidebar.posts,
      sidebar.isLoadingPost,
      sidebar.fbLives,
      sidebar.loadingFbLives,
      sidebar.qCustomers,
      sidebar.autoReplies,
      sidebar.outOfStocks,
      sidebar.setOutOfStocks,
      sidebar.fetchLiveVideos,
      sidebar.upsertActiveLive,
      sidebar.fetchQueueCustomers,
    ],
  );
  return <SaleSidebarContext.Provider value={value}>{children}</SaleSidebarContext.Provider>;
}

function SaleProductsProvider({ children }: { children: ReactNode }) {
  const products = useSaleProductsData();
  const { alphabets } = useSaleVariants();
  const value = useMemo<SaleProductsContextValue>(
    () => ({ ...products, alphabets }),
    [
      products.products,
      products.totalProduct,
      products.isLoadingProduct,
      products.isRefreshingProduct,
      products.offsetProduct,
      products.limitProduct,
      products.search,
      products.onlyInStock,
      products.orderingColumn,
      products.orderingDirection,
      products.isPinnedOnly,
      products.selectedLetter,
      products.rows,
      products.onPageChange,
      products.refreshSaleProduct,
      products.loadVariantStock,
      products.patchProductPinState,
      alphabets,
    ],
  );
  return <SaleProductsContext.Provider value={value}>{children}</SaleProductsContext.Provider>;
}

function SalePinsProvider({ children }: { children: ReactNode }) {
  const { products, patchProductPinState, refreshSaleProduct } = useSaleProducts();
  const pins = useSalePinsData({ products, patchProductPinState, refreshSaleProduct });
  const value = useMemo(
    () => pins,
    [
      pins.pinnedVariants,
      pins.activePinTab,
      pins.setActivePinTab,
      pins.pinnedVariantTabs,
      pins.pinnedIdSet,
      pins.isPinned,
      pins.handleTogglePin,
      pins.handleRemovePin,
      pins.handleUnpinAll,
      pins.handleReorder,
      pins.handleRefreshPins,
      pins.refreshPinVariants,
      pins.isLoadingPins,
    ],
  );
  return <SalePinsContext.Provider value={value}>{children}</SalePinsContext.Provider>;
}

function SaleInvalidationBridge({
  registerActions,
}: {
  registerActions?: (actions: SaleInvalidationActions) => void;
}) {
  const { handleRefreshPins } = useSalePins();
  const { refreshSaleProduct, loadVariantStock } = useSaleProducts();
  const { fetchLiveVideos } = useSaleSidebarData();
  const { refresh: refreshOrderToday } = useSaleOrderSummaryContext();

  useEffect(() => {
    if (!registerActions) return;
    registerActions({
      refreshPins: handleRefreshPins,
      refreshSaleProduct: () => {
        void refreshSaleProduct();
      },
      loadVariantStock,
      fetchLiveVideos,
      refreshOrderToday: async () => {
        await refreshOrderToday();
      },
    });
  }, [registerActions, handleRefreshPins, refreshSaleProduct, loadVariantStock, fetchLiveVideos, refreshOrderToday]);

  return null;
}

export const SaleProviders = memo(function SaleProviders({
  children,
  scheduleInvalidation,
  registerActions,
}: SaleProvidersProps) {
  return (
    <SaleOrderSummaryProvider>
      <SaleSidebarProvider scheduleInvalidation={scheduleInvalidation}>
        <SaleProductsProvider>
          <SalePinsProvider>
            <SaleInvalidationBridge registerActions={registerActions} />
            {children}
          </SalePinsProvider>
        </SaleProductsProvider>
      </SaleSidebarProvider>
    </SaleOrderSummaryProvider>
  );
});

export function useSaleProducts() {
  const context = useContext(SaleProductsContext);
  if (!context) throw new Error('useSaleProducts must be used within SaleProviders');
  return context;
}

export function useSalePins() {
  const context = useContext(SalePinsContext);
  if (!context) throw new Error('useSalePins must be used within SaleProviders');
  return context;
}

export function useSaleSidebarData() {
  const context = useContext(SaleSidebarContext);
  if (!context) throw new Error('useSaleSidebarData must be used within SaleProviders');
  return context;
}

export function useSaleOrderSummaryContext() {
  const context = useContext(SaleOrderSummaryContext);
  if (!context) throw new Error('useSaleOrderSummaryContext must be used within SaleProviders');
  return context;
}
